/*
 * The browser's history source for the replay driver: the backend worker's
 * typed reader, bounds call and row counts, scoped to one gateway. Thin by
 * design — every rule about what is fetched and how it is interpreted lives in
 * replay.ts and scan.ts, where a test can reach it with fixtures instead of a
 * worker.
 */
import { inFlightLimit } from '../../core/bridge/history-typed';
import type { BackendApi } from '../../workers/backend.worker';
import type { HistorySource } from './replay';

/** Symbols counted at once. The same figure the typed reader walks with. */
const COUNTS_IN_FLIGHT = 4;

export function historyFromBackend(
  api: Pick<BackendApi, 'queryRangeTyped' | 'historyBounds'> & Partial<Pick<BackendApi, 'historyCounts'>>,
  gatewayId: string,
): HistorySource {
  const source: HistorySource = {
    bounds: (controllerId) => api.historyBounds(gatewayId, controllerId),
    range: (controllerId, symbols, t1Ms, t2Ms) => api.queryRangeTyped(gatewayId, controllerId, symbols, t1Ms, t2Ms),
  };
  // One aggregate per symbol; a backend without the call leaves the search to
  // its fixed slices.
  if (api.historyCounts) {
    source.counts = async (controllerId, symbols, t1Ms, t2Ms, bucketMs) => {
      const per = await inFlightLimit(symbols, COUNTS_IN_FLIGHT, async (symbol) => {
        const buckets = await api.historyCounts!(gatewayId, controllerId, symbol, t1Ms, t2Ms, bucketMs / 1000);
        return buckets.map((b) => ({ symbol, tsMs: b.tsMs, count: b.count }));
      });
      return per.flat();
    };
  }
  return source;
}
